import api from '../../utils/api';

export interface User {
  userId: string;
  name?: string | null;
  emailId?: string | null;
  photo?: string;
  location?: string;
  status: boolean;
  platforms?: string | null;
  updatedAt?: string;
  phone_number?: string;
  ABHA_ID?: string;
  dateOfBirth?: string;
  gender?: string;
  bloodGroup?: string;
  height?: number;
  weight?: number;
  emergencyContactNumber?: string;
  city?: string | null;
  fcmToken?: string | null;
  createdAt?: string;
}

export interface UserResponse {
  success: boolean;
  data: User[];
}

export interface ActiveUsersResponse {
  success: boolean;
  count: number;
  users: User[];
}

export interface UserDashboardMetrics {
  totalRegisteredUsers: number;
  totalRegisteredUsersPrev?: number;
  blockedUsers: number;
  verifiedUsers: number;
  newUsersToday: number;
  newUsersTodayPrev?: number;
  newUsersThisWeek: number;
  newUsersThisWeekPrev?: number;
  activeToday: number;
  activeTodayPrev?: number;
  active7d: number;
  active7dPrev?: number;
  active30d: number;
  active30dPrev?: number;
  userGrowthData: {
    yearWeek: string;
    registrations: number;
    compareYearWeek?: string;
    compareRegistrations?: number;
  }[];
  serviceUsageData?: { service: string; count: number; prevCount?: number }[];
  serviceUsageTrend?: { date: string; 'Ambulance Bookings': number; 'Doctor Consultations': number }[];
  activeUsersAnalytics: {
    dau: number;
    wau: number;
    mau: number;
    avgSessionsPerUser: number;
    avgSessionDurationSec: number;
  };
  activityMetrics: {
    appOpensToday: number;
    loginsToday: number;
    ordersCreatedToday: number;
    prescriptionsUploadedToday: number;
    notificationsClicked: number;
    bloodRequestsToday?: number;
    ambulanceBookingsToday?: number;
    doctorConsultationsToday?: number;
  };
  topLocationsUsers: { city: string; users: number }[];
  topLocationsOrders: { city: string; orders: number }[];
  retentionMetrics?: {
    retention1d: number;
    retention7d: number;
    retention30d: number;
    returningUsers: number;
  };
  atRiskUsers?: User[];
}

export interface UserDashboardResponse {
  success: boolean;
  data: UserDashboardMetrics;
}

export interface VisitorCountResponse {
  success: boolean;
  total: number;
  updatedAt?: string | null;
}

export interface UserEvent {
  event_id: string;
  event_type: string;
  service?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  user_id: string;
  timestamp: string;
  platform?: string | null;
  city?: string | null;
  interaction_type?: string | null;
  metadata?: {
    screen?: string;
    position?: number;
    source?: string;
    [key: string]: any;
  } | null;
}

export interface UserEventsResponse {
  success: boolean;
  data: UserEvent[];
}

// Per-user analytics (for /api/master-admin/users/:userId/analytics)
export interface UserAnalyticsBasicProfile {
  userId: string;
  name?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  city?: string | null;
  location?: string | null;
  registrationDate?: string | null;
  accountStatus: 'ACTIVE' | 'INACTIVE';
  device: string | null;
  appVersion: string | null;
  lastLogin: string | null;
  totalSessions: number;
  totalOrders: number;
  totalPayments: number;
}

export interface UserAnalyticsActivitySummary {
  lastAppOpen: string | null;
  lastLogin: string | null;
  lastLogout?: string | null;
  activeSessions?: number;
  totalSessions: number;
  averageSessionDurationSec: number;
  totalAppOpens: number;
  totalScreensViewed: number;
}

export interface UserAuthActivitySession {
  sessionId: string;
  deviceId: string | null;
  platform?: 'android' | 'ios' | 'web' | string | null;
  appVersion?: string | null;
  appBuildNumber?: string | null;
  osVersion?: string | null;
  deviceModel?: string | null;
  loginMethod?: string | null;
  userAgent?: string | null;
  status: 'ACTIVE' | 'LOGGED_OUT' | 'EXPIRED' | 'REVOKED' | string;
  loginAt: string | null;
  logoutAt: string | null;
  logoutReason?: string | null;
}

export interface UserAnalyticsAuthActivity {
  isLoggedIn: boolean;
  activeSessions: number;
  totalLoginSessions: number;
  lastLogin: string | null;
  lastLogout: string | null;
  recentSessions: UserAuthActivitySession[];
}

export interface UserAnalyticsServicesInteraction {
  rawCountsByService: Record<string, number>;
  medicineFeatureViews: number;
  bloodBankViews: number;
  ambulanceViews: number;
  doctorConsultationViews: number;
}

export interface UserAnalyticsOrdersByService {
  medicine: number;
  ambulance: number;
  bloodBank: number;
  doctorConsultation: number;
  hospital: number;
  labTest: number;
  products: number;
  medicineCompleted: number;
  medicineCancelled: number;
  medicinePending: number;
  ambulanceCompleted: number;
  ambulanceCancelled: number;
  ambulancePending: number;
  bloodBankCompleted: number;
  bloodBankCancelled: number;
  bloodBankPending: number;
  doctorConsultationCompleted: number;
  doctorConsultationCancelled: number;
  doctorConsultationPending: number;
  hospitalCompleted: number;
  hospitalCancelled: number;
  hospitalPending: number;
  labTestCompleted: number;
  labTestCancelled: number;
  labTestPending: number;
  productsCompleted: number;
  productsCancelled: number;
  productsPending: number;
}

export interface UserAnalyticsBookingHistory {
  medicineOrders: number;
  bloodRequests: number;
  ambulanceBookings: number;
  completedOrders: number;
  cancelledOrders: number;
  ordersByService?: UserAnalyticsOrdersByService;
}

export interface UserAnalyticsOngoingOrder {
  id: string;
  service: string;
  status: string;
  vendor: string | null;
}

export interface UserAnalyticsOrderByRange {
  id: string;
  service: string;
  status: string;
  amount: number;
  date: string | null;
  vendor: string | null;
}

export interface UserAnalyticsPayments {
  totalAmountSpent: number;
  totalPayments: number;
  failedPayments: number;
  refundRequests: number;
  preferredPaymentMethod: string | null;
}

export interface UserAnalyticsBehavior {
  mostUsedService: string | null;
  averageSessionTimeSec: number;
  searchesPerSession: number;
  conversionRate: number;
  metricsFormulas?: {
    avgSessionTime?: string;
    searchesPerSession?: string;
    conversionRate?: string;
    mostUsedService?: string;
  };
}

export interface UserAnalyticsRecommendations {
  recommendationsSeen: number;
  recommendationsClicked: number;
  recommendationsConverted: number;
}

export interface UserAnalyticsRisk {
  cancellationRate: number;
  failedPaymentRate: number;
  flag: 'LOW' | 'MEDIUM' | 'HIGH';
  metricsFormulas?: {
    cancellationRate?: string;
    failedPaymentRate?: string;
    riskFlag?: string;
  };
}

export interface UserAnalyticsLocation {
  mostUsedCity: string | null;
  lastKnownLocation: string | null;
  locationChanges: number;
}

export interface UserAnalyticsDevice {
  devicesUsed: string[];
  lastDevice: string | null;
  appVersionsUsed: string[];
  lastAppVersion: string | null;
  networkTypes: string[];
  lastNetworkType: string | null;
}

export interface UserAnalyticsTimelineEvent {
  eventId: string;
  eventType: string;
  eventTypeLabel?: string;
  screenLabel?: string | null;
  service?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  timestamp: string | null;
  platform?: string | null;
  trackedFrom?: string | null;
  device?: string | null;
  city?: string | null;
  interactionType?: string | null;
  appVersion?: string | null;
  networkType?: string | null;
  isConversion?: boolean;
  metadata?: Record<string, any> | null;
}

export interface UserAnalyticsSession {
  sessionId: string;
  sessionStart: string | null;
  sessionEnd: string | null;
  device?: string | null;
  platform?: string | null;
  trackedFrom?: string | null;
  appVersion?: string | null;
  city?: string | null;
  eventsCount: number;
  events: UserAnalyticsTimelineEvent[];
}

export interface UserAnalyticsDateRange {
  range: string;
  from: string;
  to: string;
}

export interface UserAnalytics {
  basicProfile: UserAnalyticsBasicProfile;
  activitySummary: UserAnalyticsActivitySummary;
  authActivity?: UserAnalyticsAuthActivity;
  servicesInteraction: UserAnalyticsServicesInteraction;
  bookingHistory: UserAnalyticsBookingHistory;
  ongoingOrders?: UserAnalyticsOngoingOrder[];
  ordersByRange: UserAnalyticsOrderByRange[];
  payments: UserAnalyticsPayments;
  behavior: UserAnalyticsBehavior;
  recommendations: UserAnalyticsRecommendations;
  risk: UserAnalyticsRisk;
  locationAnalytics: UserAnalyticsLocation;
  deviceAnalytics: UserAnalyticsDevice;
  timeline: UserAnalyticsTimelineEvent[];
  sessions?: UserAnalyticsSession[];
  dateRange?: UserAnalyticsDateRange;
}

export type SessionRangeFilter = 'day' | 'week' | 'month' | 'custom';

export interface UserAnalyticsResponse {
  success: boolean;
  message: string;
  data: UserAnalytics;
}

/** Normalize `/master-admin/users` rows so `platforms` is always a trimmed string or null. */
function normalizeUserListRow(u: User): User {
  const p = u.platforms;
  if (p == null) return { ...u, platforms: null };
  const s = String(p).trim();
  return { ...u, platforms: s.length > 0 ? s : null };
}

// Fetch all users
export const fetchUsers = async (): Promise<UserResponse> => {
  try {
    const response = await api.get<UserResponse>('/master-admin/users');
    const body = response.data;
    if (body.success && Array.isArray(body.data)) {
      return { ...body, data: body.data.map(normalizeUserListRow) };
    }
    return body;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Fetch active Android users
export const fetchActiveAndroidUsers = async (): Promise<ActiveUsersResponse> => {
  try {
    const response = await api.get('/master-admin/users/active-android');
    return response.data;
  } catch (error) {
    console.error('Error fetching active Android users:', error);
    throw error;
  }
};

// Fetch active iOS users
export const fetchActiveIOSUsers = async (): Promise<ActiveUsersResponse> => {
  try {
    const response = await api.get('/master-admin/users/active-ios');
    return response.data;
  } catch (error) {
    console.error('Error fetching active iOS users:', error);
    throw error;
  }
};

export type PlatformFilter = 'all' | 'android' | 'ios' | 'web';

export const fetchUserDashboardMetrics = async (
  platform: PlatformFilter = 'all',
  params?: {
    growthFrom?: string;
    growthTo?: string;
    usageFrom?: string;
    usageTo?: string;
    groupBy?: 'day' | 'week' | 'month';
    usageGroupBy?: 'day' | 'week' | 'month';
    compare?: boolean;
    usageCompare?: boolean;
  }
): Promise<UserDashboardResponse> => {
  try {
    const searchParams = new URLSearchParams();
    if (platform && platform !== 'all') searchParams.set('platform', platform);
    if (params?.growthFrom) searchParams.set('growthFrom', params.growthFrom);
    if (params?.growthTo) searchParams.set('growthTo', params.growthTo);
    if (params?.usageFrom) searchParams.set('usageFrom', params.usageFrom);
    if (params?.usageTo) searchParams.set('usageTo', params.usageTo);
    if (params?.groupBy) searchParams.set('groupBy', params.groupBy);
    if (params?.usageGroupBy) searchParams.set('usageGroupBy', params.usageGroupBy);
    if (params?.compare !== undefined) searchParams.set('compare', String(params.compare));
    if (params?.usageCompare !== undefined) searchParams.set('usageCompare', String(params.usageCompare));

    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get(`/master-admin/users/dashboard-metrics${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user dashboard metrics:', error);
    throw error;
  }
};

export const fetchChurnRiskUsers = async (
  platform: PlatformFilter = 'all',
  days: number = 30
): Promise<{ success: boolean; data: any[] }> => {
  try {
    const searchParams = new URLSearchParams();
    if (platform && platform !== 'all') searchParams.set('platform', platform);
    searchParams.set('days', String(days));
    const response = await api.get(`/master-admin/users/churn-risk?${searchParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching churn risk users:', error);
    throw error;
  }
};

export const fetchBlockedUsers = async (
  platform: PlatformFilter = 'all'
): Promise<{ success: boolean; data: any[] }> => {
  try {
    const searchParams = new URLSearchParams();
    if (platform && platform !== 'all') searchParams.set('platform', platform);
    const response = await api.get(`/master-admin/users/blocked?${searchParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blocked users:', error);
    throw error;
  }
};

export const fetchVisitorCount = async (): Promise<VisitorCountResponse> => {
  try {
    const response = await api.get('/master-admin/visitors/count');
    return response.data;
  } catch (error) {
    console.error('Error fetching visitor count:', error);
    throw error;
  }
};

export const fetchUserEvents = async (userId: string): Promise<UserEventsResponse> => {
  try {
    const response = await api.get(`/master-admin/users/${userId}/events`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user events:', error);
    throw error;
  }
};

export interface FetchUserAnalyticsParams {
  range?: 'day' | 'week' | 'month' | 'custom';
  from?: string;
  to?: string;
}

export const fetchUserAnalytics = async (
  userId: string,
  params?: FetchUserAnalyticsParams
): Promise<UserAnalyticsResponse> => {
  try {
    const searchParams = new URLSearchParams();
    if (params?.range) searchParams.set('range', params.range);
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get(`/master-admin/users/${userId}/analytics${query}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    throw error;
  }
};

// Delete a user
export const deleteUser = async (userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/master-admin/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Update user status
export const updateUserStatus = async (userId: string, status: boolean): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.patch(`/master-admin/users/${userId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};